// RealtimeRelay: thin WebSocket client to the Cavalry-owned relay that forwards the
// Azure Realtime events filtered out of the browser by webrtcfilter=on
// (session.updated, response.created, response.done, response.output_item.done, error).
//
// Protocol (see docs/interview/webrtcfilter-frontend-integration.md):
//   Browser -> backend: { type: "start", applicationId, sectionId, callId }, { type: "stop" }
//   backend -> browser: { type: "event", event }, { type: "error", code, message }
//
// The relayed `event` has the same shape as the original Azure event (with confidential
// fields stripped), so consumers can route it straight into RealtimeService._handleMessage.

import config from "../config";
import { AUTH_TOKEN_NAME } from "../constants";

function backoffDelay(attempt, base = 500, cap = 10000) {
  const exp = Math.min(cap, base * 2 ** attempt);
  const jitter = Math.random() * 0.3 + 0.85;
  return Math.floor(exp * jitter);
}

export class RealtimeRelay {
  /**
   * @param {{ onEvent?: (event: any) => void, onLog?: (tag: string, payload?: any) => void }} [opts]
   */
  constructor({ onEvent, onLog, onError, onOpen } = {}) {
    this._onEvent = typeof onEvent === "function" ? onEvent : () => {};
    this._onLog = typeof onLog === "function" ? onLog : () => {};
    this._onError = typeof onError === "function" ? onError : () => {};
    this._onOpen = typeof onOpen === "function" ? onOpen : () => {};
    this.ws = null;
    this._ctx = null; // { applicationId, sectionId, callId }
    this._closed = false;
    this._attempt = 0;
    this._reconnectTimer = null;
    this._everOpen = false;     // whether the relay socket has ever opened
    this._lastError = null;     // last backend/relay-side failure (for diagnostics)
  }

  _wsUrl() {
    const base = (config.apiUrl || "").replace(/^http/, "ws");
    let token = "";
    try { token = localStorage.getItem(AUTH_TOKEN_NAME) || ""; } catch { /* noop */ }
    return `${base}/realtime-relay?token=${encodeURIComponent(token)}`;
  }

  /**
   * Open the relay (if needed) and observe the given call. Safe to call repeatedly:
   * a fresh call_id on the live socket re-points the backend observer (mid-section reconnect).
   * @param {{ applicationId: string|null, sectionId: string|null, callId: string|null }} ctx
   */
  start(ctx) {
    this._ctx = ctx || null;
    this._closed = false;
    const ws = this.ws;
    if (ws && ws.readyState === WebSocket.OPEN) {
      this._sendStart();
      return;
    }
    if (ws && ws.readyState === WebSocket.CONNECTING) {
      // `start` will be sent on open with the latest _ctx.
      return;
    }
    this._connect();
  }

  /** Alias for clarity at reconnect call sites. */
  restart(ctx) { this.start(ctx); }

  _connect() {
    try {
      const ws = new WebSocket(this._wsUrl());
      this.ws = ws;
      ws.addEventListener("open", () => {
        this._attempt = 0;
        this._everOpen = true;
        this._lastError = null; // fresh connection; clear any prior failure
        this._sendStart();
        this._onOpen();
      });
      ws.addEventListener("message", (e) => this._onMessage(e));
      ws.addEventListener("close", (ev) => this._onClose(ev));
      ws.addEventListener("error", () => this._reportError({ kind: "ws_error", message: "Relay WebSocket connection error" }));
    } catch (err) {
      this._reportError({ kind: "connect_error", message: `Relay could not start: ${err?.message || String(err)}` });
      this._scheduleReconnect();
    }
  }

  _sendStart() {
    if (!this._ctx) return;
    this._safeSend({ type: "start", ...this._ctx });
  }

  _safeSend(obj) {
    try {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(obj));
    } catch (err) {
      this._onLog("RelaySendError", { message: err?.message || String(err) });
    }
  }

  _onMessage(e) {
    let msg;
    try { msg = JSON.parse(e.data); } catch { return; }
    if (msg?.type === "event" && msg.event) {
      this._onEvent(msg.event);
    } else if (msg?.type === "error") {
      // Explicit backend/observer failure reported over the relay.
      this._reportError({ kind: "relay_error", code: msg.code, message: msg.message || "Relay/observer error" });
    }
  }

  _onClose(ev) {
    this.ws = null;
    if (this._closed) return; // intentional close via stop()
    const code = ev?.code;
    const detail = { kind: "ws_close", code, reason: ev?.reason || "", wasClean: !!ev?.wasClean, everOpen: this._everOpen };
    // A close before we ever opened, or a non-normal (≠1000) close, is a backend/relay-side failure.
    if (!this._everOpen || (code && code !== 1000)) {
      this._reportError(detail);
    } else {
      this._lastError = detail;
      this._onLog("RelayClosed", detail);
    }
    this._scheduleReconnect();
  }

  // Record a backend/relay-side failure with enough detail to distinguish it from a
  // browser-side WebRTC problem, and surface it to the owner (which logs + emits it).
  _reportError(detail) {
    const enriched = {
      source: "relay",
      url: this._redactedUrl(),
      everOpen: this._everOpen,
      ...detail,
      message: detail.message || this._describeDetail(detail),
    };
    this._lastError = enriched;
    this._onError(enriched);
  }

  _redactedUrl() {
    // No token — safe to log.
    return `${(config.apiUrl || "").replace(/^http/, "ws")}/realtime-relay`;
  }

  _describeDetail(d) {
    if (!d) return this._everOpen ? "relay disconnected" : "relay not connected";
    switch (d.kind) {
      case "relay_error": return `relay reported error${d.code ? " " + d.code : ""}: ${d.message || ""}`.trim();
      case "ws_close": return `relay socket closed${d.code ? " (code " + d.code + ")" : ""}${d.reason ? ": " + d.reason : ""}`;
      case "ws_error": return "relay socket error (connection failed)";
      case "connect_error": return d.message || "relay could not start";
      default: return d.message || "relay error";
    }
  }

  // Human-readable current relay status for diagnostics (contains no secrets).
  describe() {
    if (this._lastError) return this._describeDetail(this._lastError);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return "relay connected (awaiting events)";
    return this._everOpen ? "relay disconnected" : "relay not connected";
  }

  _scheduleReconnect() {
    if (this._closed) return;
    if (this._reconnectTimer) return;
    this._attempt += 1;
    const wait = backoffDelay(this._attempt);
    this._onLog("RelayScheduleReconnect", { attempt: this._attempt, waitMs: wait });
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      if (this._closed) return;
      this._connect(); // re-sends `start` with the current _ctx on open
    }, wait);
  }

  stop() {
    this._closed = true;
    if (this._reconnectTimer) { clearTimeout(this._reconnectTimer); this._reconnectTimer = null; }
    this._safeSend({ type: "stop" });
    try { this.ws?.close(); } catch { /* noop */ }
    this.ws = null;
    this._ctx = null;
  }
}
